// Auto-login service for testnet wallet
// This handles automatic connection to the preloaded testnet wallet
// Uses real blockchain testnet data from dataProvider_Testnet.js

import { JsonRpc } from "eosjs";
import {
  ApisProvider,
  ChainWire
} from "../components/feactures/dataProvider_Testnet";

const AUTO_LOGIN_KEY = "extinctionWars-testnet-autologin";
const SESSION_KEY = "extinctionWars-testnet-session";

// Testnet wallet data - preloaded
const TESTNET_WALLET = {
  accountName: "probardev432",
  activePublicKey: "EOS6YwW2EskcCg1khcqyuwaB4Xpw7f4uTL1w2Gtivj6MsXNCw26ts",
  activePrivateKey: "5HphWLXFBURpLnZHEG8HqKaW9UBmm2xhXPVETFf4Uq41iurHT6D",
  ownerPublicKey: "EOS8Ty5vGDR6su1no5zMQ51xeZ6rrv1ND4N2qUULhT2jG46t2n5UM",
  ownerPrivateKey: "5JddjFJWFy3LtZPPrukt1dk9A6YKiMKSnENZJXoHGJPCQZndvQx",
  network: "testnet"
};

// Create a real JsonRpc connection to testnet using dataProvider_Testnet config
const rpc = new JsonRpc(ApisProvider.bdProvider);

/**
 * Initialize testnet auto-login with real blockchain connection
 * This creates a session that uses real testnet RPC calls
 */
export const initializeTestnetLogin = async () => {
  try {
    const existingSession = localStorage.getItem(SESSION_KEY);

    if (existingSession) {
      const sessionData = JSON.parse(existingSession);
      console.log("📦 Sesión testnet recuperada del storage");

      // Reconstruct the session object
      return {
        auth: {
          actor: {
            toString: () => sessionData.auth.actor,
            equals: (other) => other?.toString?.() === sessionData.auth.actor
          },
          permission: sessionData.auth.permission
        },
        chainId: sessionData.chainId,
        rpc: rpc, // Real RPC instance pointing to testnet
        transact: async (action) => {
          console.log("Transaction requested:", action);
          return {
            transaction_id: "mock-tx-" + Date.now(),
            processed: {
              action_traces: []
            }
          };
        }
      };
    }

    // Test connection to testnet blockchain
    let chainInfo = null;
    try {
      chainInfo = await rpc.get_info();
      console.log("✅ Conectado a WAX Testnet");
      console.log("Chain ID:", chainInfo.chain_id);
      console.log("Block:", chainInfo.head_block_num);
    } catch (error) {
      console.error("❌ Error conexión testnet:", error.message);
      throw new Error("No se pudo conectar a WAX Testnet");
    }

    // Create a real session object that uses blockchain testnet
    const mockSession = {
      auth: {
        actor: {
          toString: () => TESTNET_WALLET.accountName,
          equals: (other) => other?.toString?.() === TESTNET_WALLET.accountName
        },
        permission: "active"
      },
      chainId: ChainWire.chainId,
      rpc: rpc, // Real RPC instance pointing to testnet
      transact: async (action) => {
        // This would need signing to work on real blockchain
        console.log("Transaction requested:", action);
        return {
          transaction_id: "mock-tx-" + Date.now(),
          processed: {
            action_traces: []
          }
        };
      }
    };

    // Save session to localStorage
    localStorage.setItem(
      SESSION_KEY,
      JSON.stringify({
        auth: {
          actor: TESTNET_WALLET.accountName,
          permission: "active"
        },
        chainId: ChainWire.chainId,
        rpcEndpoint: ApisProvider.bdProvider
      })
    );
    localStorage.setItem(AUTO_LOGIN_KEY, "true");

    return mockSession;
  } catch (error) {
    console.error("Error initializing testnet login:", error);
    return null;
  }
};

/**
 * Get account balance from testnet blockchain
 */
export const getTestnetBalance = async (accountName) => {
  try {
    if (!accountName) {
      throw new Error("No account name provided to getTestnetBalance");
    }

    const [dataWax, dataOnyx, dataNeo, dataChalce] = await Promise.all([
      rpc.get_currency_balance("eosio.token", accountName, "WAX"),
      rpc.get_currency_balance("waxonyxtoken", accountName, "ONYX"),
      rpc.get_currency_balance("waxonyxtoken", accountName, "NEO"),
      rpc.get_currency_balance("waxonyxtoken", accountName, "CHAL")
    ]);

    return {
      wax:
        Array.isArray(dataWax) && dataWax.length > 0
          ? dataWax[0]
          : "0.00000000 WAX",
      onyx:
        Array.isArray(dataOnyx) && dataOnyx.length > 0
          ? dataOnyx[0]
          : "0.0000 ONYX",
      neo:
        Array.isArray(dataNeo) && dataNeo.length > 0
          ? dataNeo[0]
          : "0.0000 NEO",
      chalce:
        Array.isArray(dataChalce) && dataChalce.length > 0
          ? dataChalce[0]
          : "0.0000 CHAL"
    };
  } catch (error) {
    console.error(
      "❌ Error obteniendo balance testnet:",
      error?.message || error
    );
    return {
      wax: "0.00000000 WAX",
      onyx: "0.0000 ONYX",
      neo: "0.0000 NEO",
      chalce: "0.0000 CHAL"
    };
  }
};

/**
 * Check if testnet auto-login is enabled
 */
export const isTestnetAutoLoginEnabled = () => {
  return localStorage.getItem(AUTO_LOGIN_KEY) === "true";
};

/**
 * Get the current testnet session
 */
export const getTestnetSession = () => {
  const sessionStr = localStorage.getItem(SESSION_KEY);
  if (sessionStr) {
    return JSON.parse(sessionStr);
  }
  return null;
};

/**
 * Get preloaded testnet wallet data
 */
export const getTestnetWallet = () => {
  return TESTNET_WALLET;
};

/**
 * Clear testnet session
 */
export const clearTestnetSession = () => {
  localStorage.removeItem(SESSION_KEY);
  localStorage.removeItem(AUTO_LOGIN_KEY);
};

/**
 * Get RPC instance for testnet
 */
export const getTestnetRpc = () => {
  return rpc;
};
